<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SupportAPI from 'dashboard/api/support';
import SupportActionCableConnector from '../helpers/actionCable';
import SupportMessageBubble from './SupportMessageBubble.vue';
import SupportComposer from './SupportComposer.vue';

const { t } = useI18n();

const messages = ref([]);
const isLoading = ref(true);
const isSending = ref(false);
const unavailableReason = ref(null);
const messageListRef = ref(null);
const composerRef = ref(null);
let actionCableConnector = null;
let localMessageId = 0;

const REASON_BY_STATUS = { 503: 'not_configured', 403: 'not_available' };

const hasMessages = computed(() => messages.value.length > 0);

const scrollToBottom = () => {
  nextTick(() => {
    if (messageListRef.value) {
      messageListRef.value.scrollTop = messageListRef.value.scrollHeight;
    }
  });
};

const addOrUpdateMessage = message => {
  const index = messages.value.findIndex(m => m.id === message.id);
  if (index === -1) {
    messages.value.push(message);
  } else {
    messages.value.splice(index, 1, message);
  }
  scrollToBottom();
};

const connectActionCable = pubsubToken => {
  if (!pubsubToken || actionCableConnector) return;
  actionCableConnector = new SupportActionCableConnector(
    pubsubToken,
    addOrUpdateMessage
  );
};

const fetchConversation = async () => {
  try {
    const { data } = await SupportAPI.get();
    messages.value = data.messages;
    unavailableReason.value = null;
    scrollToBottom();
    connectActionCable(data.pubsub_token);
  } catch (error) {
    const reason = REASON_BY_STATUS[error?.response?.status];
    if (reason) {
      unavailableReason.value = reason;
    } else {
      useAlert(t('SUPPORT_CHAT.ERROR_LOAD'));
    }
  } finally {
    isLoading.value = false;
  }
};

const sendMessage = async ({ content, attachment, retryOf }) => {
  isSending.value = true;

  const localMessage = retryOf || {
    id: (localMessageId -= 1),
    content,
    message_type: 0,
    created_at: Math.floor(Date.now() / 1000),
    attachments: [],
    // Kept only for a possible retry — the File object itself, never sent to the server as-is.
    pendingAttachment: attachment,
  };
  localMessage.isFailed = false;
  localMessage.isSending = true;
  addOrUpdateMessage(localMessage);

  try {
    const { data } = await SupportAPI.sendMessage(
      content,
      attachment || localMessage.pendingAttachment
    );
    messages.value = messages.value.filter(m => m.id !== localMessage.id);
    addOrUpdateMessage(data);
    composerRef.value?.clear();
  } catch (error) {
    const reason = REASON_BY_STATUS[error?.response?.status];
    if (reason) {
      messages.value = messages.value.filter(m => m.id !== localMessage.id);
      unavailableReason.value = reason;
    } else {
      // Network/unknown failures keep the message visible so the user can retry
      // without retyping — the spec requires the message is never silently lost.
      localMessage.isSending = false;
      localMessage.isFailed = true;
      addOrUpdateMessage(localMessage);
      composerRef.value?.clear();
    }
  } finally {
    isSending.value = false;
  }
};

const retryMessage = message => {
  sendMessage({
    content: message.content,
    attachment: message.pendingAttachment,
    retryOf: message,
  });
};

onMounted(fetchConversation);
onBeforeUnmount(() => {
  actionCableConnector?.disconnect();
});
</script>

<template>
  <div class="flex flex-col flex-1 w-full h-full min-w-0 bg-n-background">
    <header class="px-6 py-4 border-b border-n-weak">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ $t('SUPPORT_CHAT.TITLE') }}
      </h1>
    </header>

    <div
      v-if="unavailableReason"
      class="flex-1 flex items-center justify-center px-6"
    >
      <p class="text-n-slate-11 text-sm text-center">
        {{
          unavailableReason === 'not_available'
            ? $t('SUPPORT_CHAT.ERROR_NOT_AVAILABLE')
            : $t('SUPPORT_CHAT.ERROR_NOT_CONFIGURED')
        }}
      </p>
    </div>

    <template v-else>
      <div
        ref="messageListRef"
        class="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4"
      >
        <div
          v-if="!isLoading && !hasMessages"
          class="flex-1 flex items-center justify-center"
        >
          <p class="text-n-slate-11 text-sm text-center">
            {{ $t('SUPPORT_CHAT.EMPTY_STATE') }}
          </p>
        </div>

        <SupportMessageBubble
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :disabled="isSending"
          @retry="retryMessage(message)"
        />
      </div>

      <SupportComposer
        ref="composerRef"
        :is-sending="isSending"
        @send="sendMessage"
      />
    </template>
  </div>
</template>
